import React, { useContext, useEffect, useState } from "react";

import { Context } from "@reactivated";

import { SwitchLocaleModal } from "./SwitchLocaleModal";
import {
    LocaleModalStateContext,
    LocaleModalStateProvider,
    getShowLocaleModalKey,
} from "./dismissedLocales";

const SwitchLocaleModalWithLocaleModalStateContext = () => {
    const { suggested_locale, LOCALE, countries } = useContext(Context);
    const localeModalState = useContext(LocaleModalStateContext);

    const [isOpen, setIsOpen] = useState(false);

    // Initialize loaded state after storage has been read
    useEffect(() => {
        if (!localeModalState.value.loaded) {
            localeModalState.update((draft) => {
                draft.loaded = true;
            });
        }
    }, [localeModalState.value.loaded]);

    useEffect(() => {
        if (!suggested_locale) return;
        // Wait for storage to load before making decisions
        if (!localeModalState.value.loaded) return;

        const key = getShowLocaleModalKey(suggested_locale, LOCALE);
        const isDismissed = localeModalState.value.dismissals[key];

        if (isDismissed === undefined) {
            // First time seeing this suggestion - not dismissed yet, so show modal
            localeModalState.update((draft) => {
                draft.dismissals[key] = false;
            });
            setIsOpen(true);
        } else {
            setIsOpen(!isDismissed);
        }
    }, [suggested_locale, LOCALE, localeModalState.value]);

    const handleOpenChange = (isOpen: boolean) => {
        if (isOpen || !suggested_locale) return;

        const key = getShowLocaleModalKey(suggested_locale, LOCALE);

        localeModalState.update((draft) => {
            draft.dismissals[key] = true;
        });
        setIsOpen(false);
    };

    const handleSubmit = (selectedLocale: string) => {
        // If user selects their current locale, treat it as a dismissal
        if (selectedLocale === LOCALE && suggested_locale) {
            const key = getShowLocaleModalKey(suggested_locale, LOCALE);
            localeModalState.update((draft) => {
                draft.dismissals[key] = true;
            });
            setIsOpen(false);
            return;
        }

        const selectedCountry = countries.find(
            (c) => c.language_code === selectedLocale,
        );
        if (selectedCountry) {
            window.location.href = selectedCountry.url;
        }
    };

    return (
        <SwitchLocaleModal
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            countries={countries}
            suggestedLocale={suggested_locale}
            onSubmit={handleSubmit}
        />
    );
};

export const SwitchLocaleModalWithContext = () => {
    return (
        <LocaleModalStateProvider>
            <SwitchLocaleModalWithLocaleModalStateContext />
        </LocaleModalStateProvider>
    );
};
